import React from 'react';
import { DollarSign, Users, TrendingUp, Award, Briefcase, Zap } from 'lucide-react';
import { Doughnut } from 'react-chartjs-2'; // Asumo que usas Chart.js
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js/auto';
import StatusBadge from '../../components/common/StatusBadge.js';
import LoadingScreen from '../../components/LoadingScreen.js';
import AgreementProgressBarVisual from '../../utils/AgreementProgressBar.js'; // Corregido para importar el componente correcto
import { distributeCommissionsToAgreements, getAgreementProgressDetails } from '../../utils/agreementUtils.js'; // Importar la función de distribución
import { KpiCard } from '../../components/common/KpiCard.js';



// Registrar los componentes de Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

// Normaliza entradas numéricas que pueden venir como string o null.
const toNumberSafe = (value, fallback = 0) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const formatCurrency = (value) => `$${toNumberSafe(value).toFixed(2)}`;

// --- NUEVOS COMPONENTES MEJORADOS PARA GRÁFICOS ---

// Componente para el gráfico de distribución de capital mejorado
const CapitalDistributionChart = ({ chartData }) => {
    const datasetData = chartData?.datasets?.[0]?.data || [];
    const totalCapitalRaw = Array.isArray(datasetData) ? datasetData.reduce((a, b) => a + toNumberSafe(b), 0) : 0;
    const totalCapital = toNumberSafe(totalCapitalRaw);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    boxWidth: 15,
                    font: { size: 14 },
                    color: '#4E4035'
                },
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.label || '';
                        if (label) { label += ': '; }
                        if (context.parsed !== null) {
                            label += new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(context.parsed);
                        }
                        return label;
                    }
                }
            }
        },
    };

    const data = {
        labels: chartData?.labels || [],
        datasets: chartData?.datasets ? [
            {
                ...chartData.datasets[0],
                backgroundColor: ['#1F4A37', '#F8B400'], // Verde corporativo y un dorado/amarillo contrastante
                borderColor: '#F8F0E0', // Color crema del fondo para un borde sutil
                borderWidth: 4,
                hoverOffset: 8
            },
        ] : [],
    };

    return (
        <div className="chart-card">
            <h3 className="chart-title">Distribución de aporte</h3>
            <div className="doughnut-chart-container">
                <Doughnut data={data} options={options} />
                <div className="doughnut-center-text">
                    <span>Monto activo</span>
                    <strong>{formatCurrency(totalCapital)}</strong>
                </div>
            </div>
        </div>
    );
};

// Componente para el resumen de ganancias mejorado
const EarningsSummary = ({ financialSummary }) => {
    const totalEarnings = toNumberSafe(financialSummary?.grossEarnings);
    const totalROI = toNumberSafe(financialSummary?.totalROI);
    const totalCommissions = toNumberSafe(financialSummary?.totalCommissions);
    const roiPercentage = totalEarnings > 0 ? (totalROI / totalEarnings) * 100 : 0;
    const commissionsPercentage = totalEarnings > 0 ? (totalCommissions / totalEarnings) * 100 : 0;
    return (
        <div className="summary-card">
            <h3 className="chart-title">Resumen de resultados</h3>
            <div className="summary-total">
                <span>Resultado acumulado</span>
                <span className="total-value">{formatCurrency(totalEarnings)}</span>
            </div>
            <div className="summary-item">
                <div className="summary-label">
                    <TrendingUp size={20} className="summary-icon roi" />
                    <span>Resultado del acuerdo</span> {/* No necesita optional chaining aquí, ya se validó */}
                </div>
                <span className="summary-value">{formatCurrency(totalROI)}</span>
            </div>
            <div className="summary-item">
                <div className="summary-label">
                    <Award size={20} className="summary-icon commission" />
                    <span>Reconocimientos</span> {/* No necesita optional chaining aquí, ya se validó */}
                </div>
                <span className="summary-value">{formatCurrency(totalCommissions)}</span>
            </div>
            {totalEarnings > 0 && (
                <div className="earnings-breakdown-bar">
                    <div className="roi-bar" style={{ width: `${roiPercentage}%` }} title={`Resultados del acuerdo: ${roiPercentage.toFixed(1)}%`}></div>
                    <div className="commission-bar" style={{ width: `${commissionsPercentage}%` }} title={`Comisiones: ${commissionsPercentage.toFixed(1)}%`}></div>
                </div>
            )}
        </div>
    );
};

const DashboardContent = ({ kpiData, chartData, financialSummary, agreements, transactions, profileData, onViewAllClick, showTeamAndCareer }) => {
    // --- INTEGRACIÓN: Lógica de distribución de comisiones para el Dashboard ---
    // Se mueve esta llamada a useMemo al nivel superior del componente, antes de cualquier retorno condicional.
    const commissionAllocation = React.useMemo(() => {
        const safeAgreements = Array.isArray(agreements) ? agreements : [];
        const safeTransactions = Array.isArray(transactions) ? transactions : [];
        const totalCommissions = financialSummary?.totalCommissions || 0;

        if (safeAgreements.length === 0) return new Map();
        return distributeCommissionsToAgreements(safeAgreements, totalCommissions, safeTransactions);
    }, [agreements, financialSummary, transactions]);

    // --- MODIFICACIÓN: Filtrar para mostrar solo acuerdos activos en el dashboard ---
    const latestAgreements = React.useMemo(() => {
        const safeAgreements = Array.isArray(agreements) ? agreements : [];
        const safeTransactions = Array.isArray(transactions) ? transactions : [];

        const activeAgreements = safeAgreements.filter(agreement => {
            // Un acuerdo está "activo" para el dashboard si su estado es 'aprobado'
            if (agreement.status !== 'aprobado') {
                return false;
            }
            const allocatedCommission = commissionAllocation.get(agreement.id) || 0;
            const { isComplete } = getAgreementProgressDetails(agreement, allocatedCommission, safeTransactions);
            return !isComplete;
        });

        // Ordenar por fecha de creación descendente y tomar los últimos 4
        return [...activeAgreements].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)).slice(0, 4);
    }, [agreements, commissionAllocation, transactions]);

    if (!kpiData || !chartData || !financialSummary) {
        // Antes se mostraba un esqueleto (DashboardSkeleton) con grillas que dependen del ancho
        // del contenedor. Al montarse justo cuando el layout todavía se está acomodando, esas
        // grillas a veces se medían con ancho 0 y se veía roto (una columna angosta vacía). La
        // pantalla de carga con el logo es un overlay fijo a toda la pantalla, no depende de
        // ningún cálculo de layout externo, así que no tiene ese problema.
        return <LoadingScreen message="Cargando tu panel..." />;
    }
    const kpiTodaysEarnings = toNumberSafe(kpiData?.todaysEarnings);
    const kpiActiveCapital = toNumberSafe(kpiData?.activeCapital);
    const kpiWithdrawable = toNumberSafe(kpiData?.withdrawableBalance);

    return (
        <>


            {/* --- Franja verde única: arranca con "Balance Retirable" (el número que más le
                 importa al usuario) y el mismo fondo verde sigue de punta a punta detrás de las
                 otras 4 tarjetas, que quedan apoyadas encima de ese fondo compartido en vez de
                 flotar sueltas sobre el fondo crema de la página. En mobile se apila en columna
                 y cada tarjeta vuelve a tener su propio fondo (ver Dashboard.css). --- */}
            <div className="kpi-band">
                <KpiCard
                    title="Disponible para retiro"
                    value={formatCurrency(kpiWithdrawable)}
                    icon={<DollarSign size={24} />}
                    color="#16a34a"
                    isHighlighted={true}
                />
                <div className={`kpi-band__others${showTeamAndCareer ? '' : ' kpi-band__others--two'}`}>
                    <KpiCard
                        title="Resultados de hoy"
                        value={formatCurrency(kpiTodaysEarnings)}
                        icon={<TrendingUp size={24} />}
                        color="#2563eb"
                    />
                    <KpiCard
                        title="Monto activo"
                        value={formatCurrency(kpiActiveCapital)}
                        icon={<Briefcase size={24} />}
                        color="#f59e0b"
                    />
                    {showTeamAndCareer && <>
                    <KpiCard
                        title="Miembros de Equipo"
                        value={kpiData?.teamSize || 0}
                        icon={<Users size={24} />}
                        color="#9333ea"
                    />
                    {/* KPI de Rango */}
                    <KpiCard
                        title={(() => {
                            // Obtener el rango actual del usuario
                            const points = profileData?.points || 0;
                            // Simular lógica de rango (debería ser igual que en MyRank)
                            const ranks = profileData?.ranksList || [];
                            let current = null;
                            for (let i = 0; i < ranks.length; i++) {
                                if (points >= ranks[i].pointsRequired) {
                                    current = ranks[i];
                                } else {
                                    break;
                                }
                            }
                            return current?.name || 'Rango';
                        })()}
                        value={profileData?.points ? `${profileData.points} pts` : '0 pts'}
                        icon={<Award size={24} />}
                        color="#64748b"
                    />
                    </>}
                </div>
            </div>

            <div className="dashboard-charts-grid" style={{ marginTop: '0.5rem' }}>
                <CapitalDistributionChart chartData={chartData} />
                <EarningsSummary financialSummary={financialSummary} />
            </div>

            <h2 className="section-title" style={{ marginTop: '2rem' }}>Mis Últimos Acuerdos</h2>
            <div className="section-header">
                <button onClick={onViewAllClick} className="view-all-button">Ver todos</button>
            </div>
            
            <div className="agreements-grid">
                {latestAgreements.length === 0 ? (
                    <p style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#666' }}>No tienes acuerdos recientes.</p>
                ) : (
                    latestAgreements.map(agreement => {
                        // Obtener la comisión específica para este acuerdo
                        const allocatedCommission = commissionAllocation.get(agreement.id) || 0;
                        
                        // --- CORRECCIÓN: Calcular las ganancias totales usando la utilidad ---
                        const { totalEarnings } = getAgreementProgressDetails(agreement, allocatedCommission, transactions);

                        return (
                            <div key={agreement.id} className="agreement-card">
                                <div className="agreement-card-header">
                                    <div className="agreement-info-left">
                                        <h4 className="agreement-id">Acuerdo #{agreement.id.substring(0, 6).toUpperCase()}</h4>
                                        <span className="agreement-date">{new Date(agreement.createdAt.seconds * 1000).toLocaleDateString('es-ES')}</span>
                                    </div>
                                    <div className="agreement-info-right">
                                        <StatusBadge status={agreement.status} />
                                        {agreement.acceleratorActive && (
                                            <span className="accelerator-badge"><Zap size={14} /> Acelerado</span>
                                        )}
                                    </div>
                                </div>
                                <div className="agreement-card-body">
                                    {/* --- NUEVA SECCIÓN DE CAPITAL Y META --- */}
                                    <div className="summary-info-box">
                                        <div className="summary-column capital-column">
                                            <span className="summary-label">Monto activo</span>
                                            <span className="summary-value">{formatCurrency(agreement.netInvestmentAmount || agreement.amount || 0)}</span>
                                        </div>
                                        <div className="summary-column target-column">
                                            <span className="summary-label">Meta (200%)</span>
                                            <span className="summary-value target-value">{formatCurrency((toNumberSafe(agreement.netInvestmentAmount || agreement.amount || 0) * 2))}</span>
                                            <div className="sub-summary-item">
                                                <span>Resultado acumulado:</span>
                                                <strong>{formatCurrency(totalEarnings)}</strong>
                                            </div>
                                        </div>
                                    </div>
                                    {/* La barra de progreso solo se muestra para acuerdos aprobados */}
                                    {agreement.status === 'aprobado' && (
                                        <AgreementProgressBarVisual
                                            agreement={agreement}
                                            userCommissions={allocatedCommission} // La comisión ya calculada para este acuerdo
                                            transactions={transactions}
                                            styleType="detailed-card"
                                            isLightBg={true}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </>
    );
};

export default DashboardContent;